import {
  Grid,
  IconButton,
  TextField,
  LinearProgress,
  Card,
  CardContent,
  Stack,
  Typography,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import Box from "@mui/material/Box";
import { useTheme } from "@emotion/react";
import { DataGrid, GridPagination } from "@mui/x-data-grid";
import { BiEdit } from "react-icons/bi";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  CapitalAmount,
  setBalanceDialog,
  setSearchBody,
} from "../../../../../app/slices/capitalAmountSlice";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import CapitalAmountBalanceForm from "../../form/balance";
import { GiPayMoney, GiReceiveMoney } from "react-icons/gi";
import { MdOutlineClear } from "react-icons/md";
import { FaMoneyCheckAlt } from "react-icons/fa";
import Permission from "../../../../../components/userPermission/userPermission";
import { HiOutlineBanknotes } from "react-icons/hi2";
import {
  FILTERS_SX,
  MobileAction,
  MobileEmpty,
  MobileListItem,
  MobileListShell,
  MobilePagination,
  MobileSummaryCard,
  TONE,
} from "../../../../../components/MobileList";

const CapitalAmountTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const _CapitalAmounts = useSelector(
    (state) => state.capitalAmount.capitalAmounts,
  );
  const loading = useSelector(
    (state) => state.capitalAmount.capitalAmounts.loading,
  );
  const _BalanceDialog = useSelector(
    (state) => state.capitalAmount.balance.dialog,
  );

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(15);
  const [currentPage, setCurrentPage] = useState(0);

  const handlePageChange = (newPage) => {
    dispatch(
      CapitalAmount.getAll({
        search: search,
        skip: newPage * page,
        take: page,
      }),
    );
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    dispatch(
      CapitalAmount.getAll({
        search: search,
        skip: 0,
        take: newPageSize,
      }),
    );
    setPage(newPageSize);
    setCurrentPage(0);
  };

  const columns = [
    {
      field: "index",
      headerName: "#",
      flex: 0.2,
    },
    {
      field: "name",
      headerName: "الاسم",
      flex: 1,
    },
    {
      field: "balance",
      headerName: "الرصيد",
      flex: 1,
      renderCell: (params) =>
        parseInt(params.row.balance ?? 0).toLocaleString(),
    },
    {
      field: "note",
      headerName: "الملاحظات",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.row.note}>
          <Typography variant="body" noWrap>
            {params.row.note}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "created_at",
      headerName: "التاريخ",
      flex: 1,
      renderCell: (params) => (
        <Stack spacing={1}>
          <Typography>
            {dayjs(params.row.created_at).format("YYYY-MM-DD")}
          </Typography>
          <Typography>
            {dayjs(params.row.created_at).format("hh:mm a")}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "operations",
      headerName: "العمليات",
      flex: 1,
      renderCell: (params) => {
        return (
          <>
            <Permission permission="capital_amount_edit">
              <Tooltip title="اضافة رصيد">
                <IconButton
                  variant="contained"
                  color="info"
                  onClick={() =>
                    dispatch(
                      setBalanceDialog({
                        id: params.row.id,
                        type: "deposit",
                      }),
                    )
                  }
                >
                  <GiReceiveMoney />
                </IconButton>
              </Tooltip>
            </Permission>
            <Permission permission="capital_amount_edit">
              <Tooltip title="سحب رصيد">
                <IconButton
                  variant="contained"
                  color="error"
                  onClick={() =>
                    dispatch(
                      setBalanceDialog({
                        id: params.row.id,
                        type: "withdraw",
                      }),
                    )
                  }
                >
                  <GiPayMoney />
                </IconButton>
              </Tooltip>
            </Permission>
            <Tooltip title="المعاملات المالية">
              <IconButton
                variant="contained"
                onClick={() =>
                  navigate(
                    "/app/suppliers&bills/capital-amount/transactions/" +
                      params.row.id
                  )
                }
              >
                <FaMoneyCheckAlt />
              </IconButton>
            </Tooltip>
            <Permission permission="capital_amount_edit">
              <Tooltip title="تعديل">
                <IconButton
                  onClick={() => dispatch(CapitalAmount.getById(params.row.id))}
                >
                  <BiEdit />
                </IconButton>
              </Tooltip>
            </Permission>
          </>
        );
      },
    },
  ];

  useEffect(() => {
    const timeout = setTimeout(() => {
      dispatch(CapitalAmount.getAll({ search: search, skip: 0, take: page }));
      dispatch(setSearchBody({ search: search, skip: 0, take: page }));
      setCurrentPage(0);
    }, 500);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, search]);

  function CustomFooter() {
    return (
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", alignItems: "center" }}
      >
        <Typography color="primary" sx={{ marginX: 2 }}>
          المجموع الكلي : {(_CapitalAmounts.total_amount ?? 0).toLocaleString()}{" "}
          (IQD)
        </Typography>
        <GridPagination />
      </Stack>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box>{_BalanceDialog && <CapitalAmountBalanceForm />}</Box>
        <Grid
          container
          rowSpacing={1}
          columnSpacing={2}
          sx={smallScreen ? FILTERS_SX : undefined}
        >
          <Grid item xs={10} md={3}>
            <TextField
              fullWidth
              size="small"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
              }}
              label="بحث عن الاسم"
            />
          </Grid>
          <Grid item xs={2} md={2.5}>
            <Stack direction="row" spacing={0}>
              <Tooltip title="الغاء الفلتر">
                <IconButton
                  onClick={() => {
                    setSearch("");
                    setPage(15);
                    setCurrentPage(0);
                  }}
                >
                  <MdOutlineClear />
                </IconButton>
              </Tooltip>
            </Stack>
          </Grid>
          {smallScreen ? (
            <Grid item xs={12}>
              <MobileListShell loading={loading}>
                <MobileSummaryCard
                  value={(_CapitalAmounts.total_amount ?? 0).toLocaleString()}
                  icon={<HiOutlineBanknotes />}
                />
                {_CapitalAmounts.data?.length > 0 ? (
                  <>
                    {_CapitalAmounts.data.map((capitalAmount, index) => (
                      <MobileListItem
                        key={index}
                        avatar={<HiOutlineBanknotes />}
                        title={capitalAmount?.name}
                        subtitle={dayjs(capitalAmount.created_at).format(
                          "YYYY-MM-DD · hh:mm a"
                        )}
                        highlight={{
                          label: "الرصيد",
                          value: parseInt(
                            capitalAmount?.balance ?? 0
                          ).toLocaleString(),
                          rawValue: capitalAmount?.balance,
                          unit: "IQD",
                          tone: "auto",
                        }}
                        note={capitalAmount?.note}
                        actions={
                          <>
                            <Permission permission="capital_amount_edit">
                              <MobileAction
                                title="اضافة رصيد"
                                tone={TONE.success}
                                icon={<GiReceiveMoney />}
                                onClick={() =>
                                  dispatch(
                                    setBalanceDialog({
                                      id: capitalAmount.id,
                                      type: "deposit",
                                    }),
                                  )
                                }
                              />
                            </Permission>
                            <Permission permission="capital_amount_edit">
                              <MobileAction
                                title="سحب رصيد"
                                tone={TONE.danger}
                                icon={<GiPayMoney />}
                                onClick={() =>
                                  dispatch(
                                    setBalanceDialog({
                                      id: capitalAmount.id,
                                      type: "withdraw",
                                    }),
                                  )
                                }
                              />
                            </Permission>
                            <MobileAction
                              title="المعاملات المالية"
                              icon={<FaMoneyCheckAlt />}
                              onClick={() =>
                                navigate(
                                  "/app/suppliers&bills/capital-amount/transactions/" +
                                    capitalAmount.id
                                )
                              }
                            />
                            <Permission permission="capital_amount_edit">
                              <MobileAction
                                title="تعديل"
                                tone={TONE.warning}
                                icon={<BiEdit />}
                                onClick={() =>
                                  dispatch(
                                    CapitalAmount.getById(capitalAmount.id)
                                  )
                                }
                              />
                            </Permission>
                          </>
                        }
                      />
                    ))}
                    <MobilePagination
                      count={_CapitalAmounts.total ?? 0}
                      rowsPerPage={page}
                      page={currentPage}
                      onPageChange={(e, newPage) => handlePageChange(newPage)}
                      rowsPerPageOptions={[15, 25, 50, 100]}
                      onRowsPerPageChange={(event) => {
                        handlePageSizeChange(parseInt(event.target.value, 10));
                      }}
                    />
                  </>
                ) : (
                  <MobileEmpty />
                )}
              </MobileListShell>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <Box sx={{ height: "70vh", width: "100%" }}>
                <DataGrid
                  rows={
                    _CapitalAmounts.data?.map((x, index) => ({
                      ...x,
                      index: index + 1,
                    })) ?? []
                  }
                  columns={columns}
                  pageSize={page}
                  page={currentPage}
                  rowCount={_CapitalAmounts.total}
                  loading={loading}
                  rowHeight={50}
                  pagination
                  paginationMode="server"
                  selectionModel={[]}
                  onPageChange={(newPage) => handlePageChange(newPage)}
                  rowsPerPageOptions={[15, 25, 50, 100]}
                  onPageSizeChange={(newPageSize) =>
                    handlePageSizeChange(newPageSize)
                  }
                  components={{
                    LoadingOverlay: LinearProgress,
                    Footer: CustomFooter,
                  }}
                />
              </Box>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default CapitalAmountTable;
